/* CompareConversionWidget — Admin Overview tile.
 *
 * Closes the loop on the side-by-side compare feature: shows order count
 * + attributed revenue + top-winning products driven by visitors who
 * tapped "Buy the winner" in the Compare modal. Filters orders whose
 * line items carry `variant_summary` starting with "Compare winner".
 *
 * Date range is OPTIONAL — defaults to all-time so admins land on the
 * total-impact number, with from/to inputs to drill into a specific
 * period (e.g. since-launch, last-month, single-week).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Trophy, RefreshCw, ShoppingCart, DollarSign, TrendingUp, Sparkles, ExternalLink,
} from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

function fmtMoney(v) {
  const n = Number(v || 0);
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
}

const PRESETS = [
  { id: '7d',  label: '7d',  days: 7 },
  { id: '30d', label: '30d', days: 30 },
  { id: '90d', label: '90d', days: 90 },
  { id: 'all', label: 'All time', days: null },
];

export default function CompareConversionWidget() {
  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState({
    orders_count: 0,
    attributed_revenue: 0,
    avg_order_value: 0,
    top_winners: [],
    period: { all_time: true },
  });
  const [loading, setLoading] = useState(true);

  // When a preset is picked, derive the from/to dates so the inputs reflect
  // the active range. "All time" clears both → backend treats it as all-time.
  useEffect(() => {
    if (preset === 'all') {
      setFrom(''); setTo('');
      return;
    }
    const days = PRESETS.find((p) => p.id === preset)?.days;
    if (!days) return;
    const now = new Date();
    const fromDt = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    setFrom(fromDt.toISOString().slice(0, 10));
    setTo(now.toISOString().slice(0, 10));
  }, [preset]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to)   params.set('to', to);
      // Mirror the auth pattern from the rest of /admin: always send the
      // JWT cookie, plus the legacy admin password header when stored in
      // localStorage (works for both SuperAdmin JWT and the legacy gate).
      const headers = {};
      try {
        const pw = localStorage.getItem('zhelta_admin_pw');
        if (pw) headers['X-Admin-Password'] = pw;
      } catch { /* ignore */ }
      const r = await fetch(
        `${API}/admin/orders/compare-conversions?${params.toString()}`,
        { credentials: 'include', headers },
      );
      if (r.ok) {
        const d = await r.json();
        setData(d);
      }
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const empty = !loading && data.orders_count === 0;

  const headlineRange = useMemo(() => {
    if (data?.period?.all_time) return 'All-time';
    if (data?.period?.from && data?.period?.to) {
      return `${fmtDate(data.period.from)} → ${fmtDate(data.period.to)}`;
    }
    if (data?.period?.from) return `Since ${fmtDate(data.period.from)}`;
    if (data?.period?.to)   return `Through ${fmtDate(data.period.to)}`;
    return 'All-time';
  }, [data?.period]);

  const maxWinnerCount = Math.max(1, ...data.top_winners.map((w) => w.orders_count || 0));

  return (
    <section
      data-testid="admin-compare-conversion-widget"
      className="rounded-2xl border border-gray-200 bg-white overflow-hidden"
    >
      <header className="px-6 py-4 flex items-center justify-between bg-[#fffbe9] border-b border-[#f0e3a8] flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-[#c9a227]" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#a8871a]">Compare → Buy funnel</div>
            <h3 className="text-[15px] font-bold text-gray-900">Compare-driven purchases</h3>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPreset(p.id)}
              data-testid={`compare-conv-preset-${p.id}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                preset === p.id
                  ? 'bg-[#c9a227] text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-[#c9a227]'
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            data-testid="compare-conv-refresh"
            disabled={loading}
            className="p-1.5 rounded-full border border-gray-200 text-gray-500 hover:text-[#c9a227] hover:border-[#c9a227] transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      <div className="px-6 py-3 flex items-center gap-2 flex-wrap border-b border-gray-100">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-gray-500">Range:</span>
        <input
          type="date"
          value={from}
          onChange={(e) => { setFrom(e.target.value); setPreset('custom'); }}
          data-testid="compare-conv-from"
          className="px-2 py-1 text-[12px] rounded-md border border-gray-200 focus:outline-none focus:border-[#c9a227]"
        />
        <span className="text-gray-400 text-[12px]">→</span>
        <input
          type="date"
          value={to}
          onChange={(e) => { setTo(e.target.value); setPreset('custom'); }}
          data-testid="compare-conv-to"
          className="px-2 py-1 text-[12px] rounded-md border border-gray-200 focus:outline-none focus:border-[#c9a227]"
        />
        <span className="ml-auto text-[11px] text-gray-500">{headlineRange}</span>
      </div>

      <div className="px-6 py-4 grid grid-cols-3 gap-3 border-b border-gray-100">
        <BigStat
          icon={ShoppingCart}
          label="Orders"
          value={data.orders_count}
          testid="compare-conv-stat-orders"
        />
        <BigStat
          icon={DollarSign}
          label="Attributed revenue"
          value={fmtMoney(data.attributed_revenue)}
          testid="compare-conv-stat-revenue"
          highlight
        />
        <BigStat
          icon={TrendingUp}
          label="Avg. order value"
          value={fmtMoney(data.avg_order_value)}
          testid="compare-conv-stat-aov"
        />
      </div>

      {empty ? (
        <div className="text-center py-10 px-6" data-testid="compare-conv-empty">
          <Sparkles className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-[13px] text-gray-500">No compare-driven purchases in this range yet.</p>
          <p className="text-[11.5px] text-gray-400 mt-1">
            Visitors who side-by-side compare products and tap "Buy the winner" will land here.
          </p>
        </div>
      ) : (
        <div className="px-6 py-4" data-testid="compare-conv-list">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-gray-500 mb-2.5">
            Top winning products
          </div>
          <ul className="flex flex-col gap-2">
            {data.top_winners.map((w, i) => {
              const pdpUrl = w.slug ? `/store/p/${w.slug}` : null;
              return (
                <li
                  key={w.product_id || w.name || i}
                  data-testid={`compare-conv-winner-${i}`}
                  className="flex items-center gap-3 py-1.5 px-2 rounded-md hover:bg-[#fffbe9]/60 transition-colors"
                >
                  <span className="text-[10.5px] font-bold text-gray-400 w-5 text-right">{i + 1}</span>
                  {w.image ? (
                    <img
                      src={w.image}
                      alt={w.name}
                      className="w-10 h-10 rounded-md object-cover bg-gray-100 shrink-0"
                    />
                  ) : (
                    <span className="w-10 h-10 rounded-md bg-gray-100 grid place-items-center shrink-0">
                      <Trophy className="w-3.5 h-3.5 text-gray-300" />
                    </span>
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2 text-[13px]">
                      {pdpUrl ? (
                        <a
                          href={pdpUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-gray-900 hover:text-[#c9a227] truncate max-w-[260px] font-semibold"
                        >
                          <span className="truncate">{w.name || 'Untitled product'}</span>
                          <ExternalLink className="w-3 h-3 shrink-0" />
                        </a>
                      ) : (
                        <span className="text-gray-900 truncate font-semibold max-w-[260px]">{w.name || 'Untitled product'}</span>
                      )}
                    </span>
                    <span className="block mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <span
                        className="block h-full rounded-full"
                        style={{
                          width: `${(w.orders_count / maxWinnerCount) * 100}%`,
                          background: 'linear-gradient(90deg,#e6c35a,#c9a227)',
                        }}
                      />
                    </span>
                    <span className="text-[11px] text-gray-500 mt-1 inline-flex items-center gap-2 flex-wrap">
                      <span><ShoppingCart className="w-3 h-3 inline mr-1" />{w.orders_count} order{w.orders_count === 1 ? '' : 's'}</span>
                      <span>· {w.units} unit{w.units === 1 ? '' : 's'}</span>
                      <span>· <strong className="text-[#a8871a]">{fmtMoney(w.revenue)}</strong></span>
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}

function BigStat({ icon: Icon, label, value, highlight = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`flex items-start gap-3 p-3 rounded-lg ${highlight ? 'bg-[#fffbe9] border border-[#f0e3a8]' : 'border border-gray-100'}`}
    >
      <span className={`h-9 w-9 rounded-md grid place-items-center shrink-0 ${highlight ? 'bg-[#c9a227] text-white' : 'bg-[#c9a227]/10 text-[#c9a227]'}`}>
        <Icon className="w-4 h-4" />
      </span>
      <span className="flex flex-col leading-tight">
        <span className="text-[18px] font-black text-gray-900">{value}</span>
        <span className="text-[10.5px] uppercase tracking-wider text-gray-500">{label}</span>
      </span>
    </div>
  );
}
